import {
  APIProvider,
  Map,
  Marker,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { useEnv } from "~/hooks/use-env";
import { useEffect, useMemo, useState } from "react";

type PlanMapProps = {
  exactDetailsHidden: boolean;
  location: {
    isGeneralArea: boolean;
    name: string;
    suburb?: string | null;
    state?: string | null;
    coordinates?: {
      latitude: number;
      longitude: number;
    } | null;
  };
};

function SuburbCircle({
  center,
  radius = 1500,
}: {
  center: { lat: number; lng: number };
  radius?: number;
}) {
  const map = useMap();
  const mapsLibrary = useMapsLibrary("maps");
  const [circle, setCircle] = useState<google.maps.Circle | null>(null);

  useEffect(() => {
    if (!map || !mapsLibrary) return;

    const nextCircle = new mapsLibrary.Circle({
      map,
      center,
      radius,
      fillColor: "#F3564F",
      fillOpacity: 0.15,
      strokeColor: "#F3564F",
      strokeOpacity: 0.4,
      strokeWeight: 2,
    });

    setCircle(nextCircle);

    return () => {
      nextCircle.setMap(null);
    };
  }, [map, mapsLibrary, center, radius]);

  useEffect(() => {
    if (!circle) return;

    circle.setCenter(center);
    circle.setRadius(radius);
  }, [circle, center, radius]);

  return null;
}

export function PlanMap({ exactDetailsHidden, location }: PlanMapProps) {
  const env = useEnv();
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const latitude = location.coordinates?.latitude;
  const longitude = location.coordinates?.longitude;
  const center = useMemo(
    () =>
      latitude === undefined || longitude === undefined
        ? null
        : { lat: latitude, lng: longitude },
    [latitude, longitude],
  );

  if (!isClient || !center || !env?.GOOGLE_MAPS_API_KEY) {
    return (
      <div className="w-full h-[300px] bg-butter-cream flex items-center justify-center">
        <p className="text-butter-green font-sans">Map unavailable</p>
      </div>
    );
  }

  return (
    <div className="w-full h-[300px]">
      <APIProvider apiKey={env.GOOGLE_MAPS_API_KEY}>
        <Map
          defaultCenter={center}
          defaultZoom={
            location.isGeneralArea ? 10 : exactDetailsHidden ? 13 : 15
          }
          gestureHandling="cooperative"
          disableDefaultUI={true}
          mapId="plan-location-map"
        >
          {location.isGeneralArea ? null : exactDetailsHidden ? (
            <SuburbCircle center={center} />
          ) : (
            <Marker position={center} />
          )}
        </Map>
      </APIProvider>
    </div>
  );
}
